import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import YouTubeCard from './YouTubeCard';
import LiveTodayPanel from './LiveTodayPanel';
import { track } from '../lib/analytics';

/**
 * One-screen hero — top: wordmark + headline + body + CTA. Below: side-by-side
 * intro video + live-today mini-dashboard. The page is then capped by the rolling
 * ticker + TrustStrip in Home.jsx to keep the whole landing in a single viewport.
 *
 * No text-shadow / glow on the body; the only accent is the Cinzel gold-gradient
 * wordmark and the accent half of the headline.
 */
export default function Hero() {
  return (
    <section className="relative">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40 pointer-events-none" />

      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 pt-4 lg:pt-5 pb-4 lg:pb-5">
        {/* Hero text — centered, compact */}
        <div className="text-center fade-in-up">
          <h2 className="font-logo gold-gradient-text text-5xl sm:text-6xl md:text-7xl leading-none">
            TALAS&nbsp;AI
          </h2>
          {/* Headline — keep "Hands-Off." together so "Off." never orphans. */}
          <h1 className="font-display mt-3 text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#F1ECDF]" style={{ textWrap: 'balance' }}>
            Auto-AI Trading.{' '}
            <span className="gold-text whitespace-nowrap">Completely Hands&#8209;Off.</span>
          </h1>
          <p className="mt-2 text-[12px] sm:text-[13px] text-[#CDBE94] max-w-2xl mx-auto leading-relaxed">
            Talas Trading Co. gives subscribers access to structured trading automation, broker-connected execution tools, risk controls, and position-management infrastructure built for beginners, busy professionals, and active traders alike.
          </p>
          <div className="mt-3 flex justify-center">
            <Link
              to="/signup"
              data-testid="hero-signup-btn"
              onClick={() => track('sign_up_click', { source: 'hero' })}
              className="cta-sheen group inline-flex items-center justify-center gap-2 px-5 py-2 bg-[#C9A24B] text-[#1a1407] font-semibold text-sm rounded-sm hover:bg-[#E8C874] transition-colors"
            >
              Sign Up Free
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Video + Live panel — equal columns, BOTH constrained to 16:9 so they're the same size */}
        <div className="mt-4 lg:mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 fade-in-up" style={{ animationDelay: '0.08s' }}>
          <YouTubeCard
            videoId="zQIYYFIqOq4"
            title="Talas AI Introduction"
            params={{ autoplay: 1, mute: 1 }}
            testid="hero-product-video"
          />
          <div className="aspect-video w-full">
            <LiveTodayPanel />
          </div>
        </div>
      </div>
    </section>
  );
}
